import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import MarketingHeader from "@/components/MarketingHeader";
import privacyCandidate from "@/legal/privacy-candidate.json";
import previousTerms from "@/legal/terms-archive/2026-09-21.1.json";
import termsManifest from "@/legal/terms-manifest.json";
import MarketingFooter from "@/components/MarketingFooter";

/** Versioned legal documents; historical Terms retain their original text. */
export default function LegalDocument({ titleKey, docKey, testId }) {
  const { t, lang: uiLang } = useI18n();
  const { search } = useLocation();
  const requestedLang = new URLSearchParams(search).get("lang");
  const lang = ["terms", "privacy"].includes(docKey) && ["it", "en"].includes(requestedLang) ? requestedLang : uiLang;
  const requestedVersion = new URLSearchParams(search).get("version");
  const versions = [termsManifest, previousTerms];
  const selectedManifest = docKey === "terms" ? versions.find(v => v.version === (requestedVersion || termsManifest.version)) : null;
  const missingVersion = docKey === "terms" && !selectedManifest;
  const archivedTerms = docKey === "terms" ? selectedManifest?.languages[lang] : (docKey === "privacy" && process.env.REACT_APP_CONTENT_CONSENTS_ENABLED === "1" ? privacyCandidate.languages[lang] : null);
  const revisionDate = new Date((docKey === "privacy" && process.env.REACT_APP_CONTENT_CONSENTS_ENABLED === "1" ? privacyCandidate.revision_date : selectedManifest?.revision_date || termsManifest.revision_date || "2026-09-21") + "T12:00:00Z").toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
    year: "numeric", month: "long", day: "numeric",
  });
  const doc = missingVersion ? null : archivedTerms ? archivedTerms.document : t(`app.legal.${docKey}`);
  const sections = Array.isArray(doc?.sections) ? doc.sections : [];

  useEffect(() => {
    const scrollToSection = () => {
      const id = window.location.hash.slice(1);
      if (id === "controller" || id === "contacts") {
        document.getElementById(id)?.scrollIntoView();
      }
    };
    scrollToSection();
    window.addEventListener("hashchange", scrollToSection);
  return () => window.removeEventListener("hashchange", scrollToSection);
  }, [docKey]);

    if (missingVersion) return <div className="nvl-container py-12" role="alert">
    <h1>{lang === "it" ? "Versione dei Termini non disponibile" : "Terms version unavailable"}</h1>
    <p>{lang === "it" ? "Non mostriamo un altro testo al posto di quello richiesto." : "We will not substitute a different text for the requested version."}</p>
    <Link to={`/legal/terms?lang=${lang}`}>{lang === "it" ? "Torna ai Termini" : "Back to Terms"}</Link>
  </div>;

  return (
    <div className="min-h-screen flex flex-col" data-testid={testId}>
      <MarketingHeader />
      <div className="nvl-container py-12 md:py-20 flex-1">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-6"
          style={{ color: "hsl(var(--nvl-ink-soft))" }}
        >
          <ArrowLeft size={14} /> {t("app.legal.backHome")}
        </Link>

        <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
          {t("landing.footer.privacy") /* section label reused */ }
        </p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl">
          {archivedTerms ? archivedTerms.title : t(`app.legal.${titleKey}`)}
        </h1>
        <p className="mt-3 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("app.legal.lastUpdated", { date: revisionDate })}
        </p>

        <div
          className="mt-6 nvl-card p-4 md:p-5 text-sm"
          style={{ borderColor: "hsl(var(--nvl-accent))" }}
        >
          <div
            className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-widest mb-2"
            style={{
              backgroundColor: "hsl(var(--nvl-accent) / 0.15)",
              color: "hsl(var(--nvl-accent))",
            }}
          >
            {archivedTerms ? archivedTerms.badge : t("app.legal.placeholderBadge")}
          </div>
          <p style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {archivedTerms ? archivedTerms.notice : t("app.legal.draftNotice")}
          </p>
        </div>

        {docKey === "terms" && <nav aria-label={lang === "it" ? "Versioni dei Termini" : "Terms versions"} className="mt-6">
          <p>{lang === "it" ? "Versione visualizzata" : "Displayed version"}: {selectedManifest.version}</p>
          {selectedManifest.version !== termsManifest.version && <p>{lang === "it" ? "Archivio storico: testo conservato con lo stato originario." : "Historical archive: text preserved with its original status."}</p>}
          {versions.map(v => <Link className="mr-4 underline" key={v.version} to={`/legal/terms?version=${encodeURIComponent(v.version)}&lang=${lang}`}>{v.version}</Link>)}
        </nav>}
        <p className="mt-8 text-base md:text-lg max-w-3xl">
          {doc?.intro}
        </p>

        <div className="mt-10 space-y-8 max-w-3xl">
          {sections.map((s, i) => (
            <section key={i} id={docKey === "privacy" ? (i === 0 ? "controller" : (process.env.REACT_APP_CONTENT_CONSENTS_ENABLED !== "1" && i === 8) ? "contacts" : undefined) : undefined} style={{ scrollMarginTop: "120px" }} data-testid={`legal-section-${docKey}-${i}`}>
              {docKey === "privacy" && process.env.REACT_APP_CONTENT_CONSENTS_ENABLED === "1" && i === 0 && <span id="contacts" />}
              <h2 className="font-display text-2xl md:text-3xl">{s.title}</h2>
              <p className="mt-3 text-sm md:text-base whitespace-pre-line" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {s.body}
              </p>
            </section>
          ))}
        </div>
      </div>
      <MarketingFooter />
    </div>
  );
}
